import React, { useEffect, useMemo, useState } from 'react'
import { vhMinus } from 'utils/textZoom'
import dayjs from 'dayjs'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, FieldValues, FormProvider } from 'react-hook-form'

import { getErrorMessage } from 'utils'
import { useSnackbar } from 'contexts'
import { useOwner, useOwnerUpdateMutations } from 'hooks/api'
import { ROUTES } from 'routes'

import { Box, Collapse, Typography } from '@mui/material'
import { CircleAlertIcon } from 'lucide-react'

import { Button, Card, SectionNav } from '@repo/ui'
import { Header, Search, StickyFormHeader, TocSpacer } from 'components'
import { OwnerEditPageSkeleton } from './OwnerEditPageSkeleton'
// Section wrapper styles, shared with the skeleton (and the product form) so they cannot drift.
import { FORM_SECTION_SX } from 'utils/formSectionSx'

import {
  OWNER_FORM_SECTIONS,
  OwnerDetailsFields,
  OwnerAddressFields,
  OwnerMarketingFields,
  useSectionScrollSpy,
} from '../CreateOwnerPage/ownerFormExports'

const COLUMN_WIDTH = 720

// VERA-914 (Stage B) — Edit Owner, mirrored onto the same section-driven form
// (left TOC + search bar) as CreateOwnerPage, so create/edit stay consistent.
// Details / Address / Marketing sections are the exact same shared components
// as CreateOwnerPage (see ownerFormExports) — only the data-fetch/populate and
// the submit mutation differ here.
export const OwnerEditPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { open: notification } = useSnackbar()
  const [search, setSearch] = useState('')

  // Fetch owner data
  const { data: ownerData, isLoading, isError } = useOwner({ ownerId: id as string })
  const owner = (ownerData?.owner ?? null) as any
  const isEmpty = ownerData === null || owner === null

  const methods = useForm({
    defaultValues: {
      full_name: '',
      email: '',
      phone: '',
      birthday: '',
      gender: '',
      country: '',
      photo: null,
      address_line: '',
      city: '',
      state: '',
      postal_code: '',
      address_country: '',
      latitude: null,
      longitude: null,
      place_id: null,
      formatted_address: null,
      email_marketing_consent: false,
      sms_marketing_consent: false,
    },
    mode: 'onChange',
  })

  const {
    handleSubmit,
    formState: { isDirty, isValid, errors },
    reset,
  } = methods

  const sectionIds = useMemo(() => OWNER_FORM_SECTIONS.map((s) => s.id), [])
  const { activeId, setActiveId, scrollToSection, suppressSpyRef } = useSectionScrollSpy(sectionIds, !isLoading)

  // Populate form with owner data when loaded
  useEffect(() => {
    if (owner) {
      const nameParts = [owner.first_name, owner.last_name].filter(
        (part): part is string => Boolean(part && part.trim().length > 0)
      )
      const fallbackName = (owner.username ?? '')
        .replace(/[^a-zA-Z\s\-']/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim()
      const fullName = nameParts.length > 0 ? nameParts.join(' ') : fallbackName

      // Birthday is displayed/edited as MM/DD/YYYY (DatePickerField's `format`) —
      // format the loaded ISO date the same way so an untouched field still
      // round-trips correctly through the MM/DD/YYYY parse in onSubmit.
      const birthday =
        owner.birthday && dayjs(owner.birthday).isValid() ? dayjs(owner.birthday).format('MM/DD/YYYY') : ''

      reset({
        full_name: fullName,
        email: owner.email || '',
        phone: owner.phone || '',
        birthday,
        gender: owner.gender || '',
        country: owner.country || '',
        photo: owner.avatar || null,
        address_line: owner.address_line || '',
        city: owner.city || '',
        state: owner.state || '',
        postal_code: owner.postal_code || '',
        address_country: owner.address_country || '',
        latitude: owner.latitude ?? null,
        longitude: owner.longitude ?? null,
        place_id: owner.place_id ?? null,
        formatted_address: owner.formatted_address ?? null,
        email_marketing_consent: Boolean(owner.email_marketing_consent),
        sms_marketing_consent: Boolean(owner.sms_marketing_consent),
      })
    }
  }, [owner, reset])

  // Update owner mutation
  const [{ updateOwner }, { isLoading: isMutationLoading }] = useOwnerUpdateMutations({
    onSuccess: () => {
      notification({
        type: 'success',
        title: 'Owner updated successfully',
      })
      // Reset the form with the updated data to mark it as not dirty
      reset(methods.getValues())
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      notification({
        type: 'error',
        title: message,
      })
    },
  })

  // Handle unsaved changes with beforeunload event
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
        return e.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  const onSubmit = handleSubmit(async (data: FieldValues) => {
    if (!id) return

    // Birthday is picked/displayed as MM/DD/YYYY (DatePickerField's `format`);
    // convert to an ISO date string (YYYY-MM-DD) for the API.
    const birthdayValue =
      data.birthday && dayjs(data.birthday, 'MM/DD/YYYY').isValid()
        ? dayjs(data.birthday, 'MM/DD/YYYY').format('YYYY-MM-DD')
        : undefined

    updateOwner({
      ownerId: id,
      fullName: data.full_name,
      birthday: birthdayValue,
      gender: data.gender === '' ? undefined : data.gender,
      email: data.email,
      phone: data.phone,
      country: data.country === '' ? undefined : data.country,
      addressLine: data.address_line || undefined,
      city: data.city || undefined,
      state: data.state || undefined,
      postalCode: data.postal_code || undefined,
      addressCountry: data.address_country === '' ? undefined : data.address_country,
      latitude: data.latitude ?? undefined,
      longitude: data.longitude ?? undefined,
      placeId: data.place_id || undefined,
      formattedAddress: data.formatted_address || undefined,
      emailMarketingConsent: data.email_marketing_consent,
      smsMarketingConsent: data.sms_marketing_consent,
      inviteOwner: false, // Never invite during edit
    })
  })

  const onCancel = () => {
    if (isDirty) {
      const shouldLeave = window.confirm('You have unsaved changes. Are you sure you want to leave without saving?')
      if (!shouldLeave) return
    }
    navigate(-1)
  }

  // ---- Filter: which sections are visible for the current search ----------
  const q = search.trim().toLowerCase()
  const isSectionVisible = useMemo(
    () => (section: (typeof OWNER_FORM_SECTIONS)[number]) => {
      if (!q) return true
      const hasError = section.fields.some((f) => Boolean((errors as any)?.[f]))
      if (hasError) return true
      return (
        section.label.toLowerCase().includes(q) ||
        section.keywords.some((k) => k.includes(q) || q.includes(k))
      )
    },
    [q, errors]
  )

  const visibleSections = OWNER_FORM_SECTIONS.filter(isSectionVisible)
  const hasErrors = Object.keys(errors).length > 0
  const sectionHasError = (section: (typeof OWNER_FORM_SECTIONS)[number]) =>
    section.fields.some((f) => Boolean((errors as any)[f]))

  const handleTocChange = (sectionId: string) => {
    suppressSpyRef.current = true
    const section = OWNER_FORM_SECTIONS.find((s) => s.id === sectionId)
    if (section && !isSectionVisible(section)) {
      setSearch('')
      setActiveId(sectionId)
      setTimeout(() => scrollToSection(sectionId), 50)
      return
    }
    scrollToSection(sectionId)
  }

  const sectionSx = FORM_SECTION_SX

  // Show 404 state for missing owner
  if (!isLoading && (isError || isEmpty)) {
    return (
      <Box sx={{ width: '100%', mb: 16, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ maxWidth: COLUMN_WIDTH, width: '100%' }}>
          <Header title="Edit owner" sx={{ pb: 0 }} />
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              The owner you're looking for doesn't exist or has been removed.
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button variant="secondary" onClick={() => navigate(ROUTES.OWNERS)}>
                Back to Owners
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <>
      {isLoading ? (
        // Footprint-parity placeholder (§19) in place of the centred <Loader />, which reserved none
        // of the 200px TOC rail, the 167px sticky header, or the three cards. See
        // OwnerEditPageSkeleton for the measurements.
        <OwnerEditPageSkeleton onBack={onCancel} />
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 6,
              width: '100%',
              // Was mb:16 — clearance for the fixed bottom action bar, which is gone.
              mb: 6,
              justifyContent: 'center',
              alignItems: { xs: 'center', md: 'stretch' },
            }}
          >
            <SectionNav
              items={OWNER_FORM_SECTIONS.map((s) => ({
                label: s.label,
                value: s.id,
                endAdornment: sectionHasError(s) ? (
                  <Box component="span" sx={{ color: 'error.main', display: 'inline-flex' }}>
                    <CircleAlertIcon size={16} strokeWidth={1.5} />
                  </Box>
                ) : undefined,
              }))}
              value={activeId}
              onChange={handleTocChange}
              onBack={onCancel}
            />

            <Box
              sx={{
                flex: 1,
                maxWidth: COLUMN_WIDTH,
                width: { xs: '100%', md: 'auto' },
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                minHeight: vhMinus(160),
              }}
            >
              <StickyFormHeader
                title="Edit owner"
                actions={
                  <>
                    <Button variant="secondary" disableElevation onClick={onCancel} disabled={isMutationLoading}>
                      Cancel
                    </Button>
                    <Button
                      disableElevation
                      disabled={isMutationLoading || !isDirty || !isValid || hasErrors}
                      onClick={onSubmit}
                    >
                      {isMutationLoading ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                }
              >
                <Search onChange={(value: string) => setSearch(value)} fullWidth={true} placeholder="Search fields..." />
              </StickyFormHeader>

              <FormProvider {...methods}>
                {/* DETAILS */}
                <Collapse in={isSectionVisible(OWNER_FORM_SECTIONS[0])} timeout={250} unmountOnExit>
                  <Box id="details" sx={sectionSx}>
                    <Card title="Details">
                      <OwnerDetailsFields methods={methods} defaultBirthday={owner?.birthday} />
                    </Card>
                  </Box>
                </Collapse>

                {/* ADDRESS */}
                <Collapse in={isSectionVisible(OWNER_FORM_SECTIONS[1])} timeout={250} unmountOnExit>
                  <Box id="address" sx={sectionSx}>
                    <Card title="Address">
                      <OwnerAddressFields methods={methods} defaultAddressLine={owner?.address_line} />
                    </Card>
                  </Box>
                </Collapse>

                {/* MARKETING */}
                <Collapse in={isSectionVisible(OWNER_FORM_SECTIONS[2])} timeout={250} unmountOnExit>
                  <Box id="marketing" sx={sectionSx}>
                    <Card title="Marketing">
                      <OwnerMarketingFields methods={methods} />
                    </Card>
                  </Box>
                </Collapse>

                {visibleSections.length === 0 && (
                  <Box sx={{ py: 10, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No fields match "{search}"
                    </Typography>
                  </Box>
                )}
              </FormProvider>
            </Box>

            <TocSpacer />
          </Box>

        </>
      )}
    </>
  )
}
